import { getApiBaseUrl } from '../lib/getApiUrl';
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DuplicateCheckRequest,
  DuplicateCheckResponse,
  DuplicateSet,
  DuplicateSetsResponse,
  DuplicateFilters,
  MergeConfig,
  MergeHistory,
  DuplicateConfig,
  DuplicateReport
} from '@/types/duplicates';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// API functions
const checkDuplicates = async (data: DuplicateCheckRequest): Promise<DuplicateCheckResponse> => {
  const response = await fetch(`${API_BASE_URL}/duplicates/check`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error('Failed to check for duplicates');
  }

  const result = await response.json();
  return result.data || result;
};

const fetchDuplicateSets = async (filters: DuplicateFilters): Promise<DuplicateSetsResponse> => {
  const params = new URLSearchParams();

  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.status) params.append('status', filters.status);
  if (filters.matchType) params.append('matchType', filters.matchType);

  const response = await fetch(`${API_BASE_URL}/duplicates/sets?${params}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch duplicate sets');
  }

  const data = await response.json();
  return data.data || data;
};

const fetchDuplicateSet = async (setId: string): Promise<DuplicateSet> => {
  const response = await fetch(`${API_BASE_URL}/duplicates/sets/${setId}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch duplicate set');
  }

  const data = await response.json();
  return data.data || data;
};

const mergeDuplicates = async (setId: string, config: MergeConfig): Promise<{ mergeHistory: MergeHistory; lead: any }> => {
  const response = await fetch(`${API_BASE_URL}/duplicates/sets/${setId}/merge`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(config)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to merge duplicates');
  }

  const data = await response.json();
  return data.data || data;
};

const updateSetStatus = async (setId: string, status: string): Promise<DuplicateSet> => {
  const response = await fetch(`${API_BASE_URL}/duplicates/sets/${setId}/status`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ status })
  });

  if (!response.ok) {
    throw new Error('Failed to update set status');
  }

  const data = await response.json();
  return data.data || data;
};

const fetchMergeHistory = async (filters?: { page?: number; limit?: number }): Promise<{ history: MergeHistory[]; pagination: any }> => {
  const params = new URLSearchParams();
  if (filters?.page) params.append('page', filters.page.toString());
  if (filters?.limit) params.append('limit', filters.limit.toString());

  const response = await fetch(`${API_BASE_URL}/duplicates/merge-history?${params}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch merge history');
  }

  const data = await response.json();
  return data.data || data;
};

const fetchDuplicateConfig = async (): Promise<DuplicateConfig> => {
  const response = await fetch(`${API_BASE_URL}/duplicates/config`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch duplicate config');
  }

  const data = await response.json();
  return data.data || data;
};

const updateDuplicateConfig = async (config: Partial<DuplicateConfig>): Promise<DuplicateConfig> => {
  const response = await fetch(`${API_BASE_URL}/duplicates/config`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(config)
  });

  if (!response.ok) {
    throw new Error('Failed to update duplicate config');
  }

  const data = await response.json();
  return data.data || data;
};

const fetchDuplicateReport = async (dateRange?: { startDate?: string; endDate?: string }): Promise<DuplicateReport> => {
  const params = new URLSearchParams();
  if (dateRange?.startDate) params.append('startDate', dateRange.startDate);
  if (dateRange?.endDate) params.append('endDate', dateRange.endDate);

  const response = await fetch(`${API_BASE_URL}/duplicates/report?${params}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch duplicate report');
  }

  const data = await response.json();
  return data.data || data;
};

// Custom hooks

/**
 * Hook for checking duplicates during lead creation
 */
export function useDuplicateCheck() {
  return useMutation({
    mutationFn: checkDuplicates
  });
}

/**
 * Hook for fetching all duplicate sets with filters
 */
export function useDuplicateSets(initialFilters?: Partial<DuplicateFilters>) {
  const [filters, setFiltersState] = useState<DuplicateFilters>({
    page: 1,
    limit: 20,
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['duplicateSets', filters],
    queryFn: () => fetchDuplicateSets(filters)
  });

  const setFilters = useCallback((newFilters: DuplicateFilters) => {
    setFiltersState({ ...newFilters, page: 1 });
  }, []);

  const updateFilter = useCallback((key: keyof DuplicateFilters, value: any) => {
    setFiltersState(prev => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : 1
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState({ page: 1, limit: 20 });
  }, []);

  const setPage = useCallback((page: number) => {
    setFiltersState(prev => ({ ...prev, page }));
  }, []);

  return {
    sets: query.data?.sets || [],
    pagination: query.data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 },
    isLoading: query.isLoading,
    error: query.error as Error | null,
    filters,
    setFilters,
    updateFilter,
    clearFilters,
    setPage,
    refetch: query.refetch
  };
}

/**
 * Hook for fetching a single duplicate set
 */
export function useDuplicateSet(setId: string | null) {
  return useQuery({
    queryKey: ['duplicateSet', setId],
    queryFn: () => fetchDuplicateSet(setId!),
    enabled: !!setId
  });
}

/**
 * Hook for merging duplicates
 */
export function useMergeDuplicates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ setId, config }: { setId: string; config: MergeConfig }) =>
      mergeDuplicates(setId, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['duplicateSets'] });
      queryClient.invalidateQueries({ queryKey: ['duplicateSet'] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['mergeHistory'] });
    }
  });
}

/**
 * Hook for updating duplicate set status
 */
export function useUpdateSetStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ setId, status }: { setId: string; status: string }) =>
      updateSetStatus(setId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['duplicateSets'] });
      queryClient.invalidateQueries({ queryKey: ['duplicateSet'] });
    }
  });
}

/**
 * Hook for fetching merge history
 */
export function useMergeHistory(filters?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['mergeHistory', filters],
    queryFn: () => fetchMergeHistory(filters)
  });
}

/**
 * Hook for fetching duplicate config
 */
export function useDuplicateConfig() {
  return useQuery({
    queryKey: ['duplicateConfig'],
    queryFn: fetchDuplicateConfig
  });
}

/**
 * Hook for updating duplicate config
 */
export function useUpdateDuplicateConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDuplicateConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['duplicateConfig'] });
    }
  });
}

/**
 * Hook for fetching duplicate report/analytics
 */
export function useDuplicateReport(dateRange?: { startDate?: string; endDate?: string }) {
  return useQuery({
    queryKey: ['duplicateReport', dateRange],
    queryFn: () => fetchDuplicateReport(dateRange)
  });
}

/**
 * Hook for getting pending duplicate count (for badge)
 */
export function usePendingDuplicateCount() {
  const { data } = useQuery({
    queryKey: ['duplicateSets', { status: 'PENDING', limit: 1 }],
    queryFn: () => fetchDuplicateSets({ status: 'PENDING', limit: 1 })
  });

  return data?.pagination?.total || 0;
}
